import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Typography, DatePicker, Select, Input, Tooltip, Button, message, Popconfirm, Row, Col, Space, Tag } from 'antd';
import type { Key } from 'react';
import { DeleteOutlined, ReloadOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import { useAppSelector, useAppDispatch } from '../../Modules/Presentation/hooks';
import { selectLogs, selectLogLoading, selectLogPagination } from '../../Modules/Log/Presentation/selectors';
import { fetchLogs, deleteLog, bulkDeleteLogs } from '../../Modules/Log/Presentation/actions';
import type { Log, GetLogsResponse, LogSummary } from '../../Modules/Log/Model/Log';
import type { LogParams } from '../../Modules/Log/Domain/Repositories/LogRepository';
import { LogRepositoryImpl } from '../../Modules/Log/Data/Repositories/LogRepositoryImpl';
import { actionMeta } from './components/helpers';
import LogTable from './components/LogTable';
import { selectAccounts } from '../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import { fetchAccounts } from '../../Modules/Account/Presentation/actions';
import { canDeleteLogs, isFullAdmin } from '../../Utils/limitedPartnerAdmin';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const logRepo = new LogRepositoryImpl();

type ActiveFilter = 'today' | 'yesterday' | 'this_month' | 'custom' | null;
type SearchField =
  | 'all'
  | 'keyword'
  | 'slot'
  | 'actor'
  | 'parent'
  | 'worker'
  | 'action'
  | 'date'
  | 'start_date'
  | 'end_date';
type AccountFilterField = 'sellerId' | 'sellerParentId' | 'creatorId' | 'actorId';
type AccountLevel = 'ADMIN' | 'DISTRIBUTOR' | 'AGENCY' | 'SELLER';

const stripLiftSuffix = (username: string): string =>
  username.endsWith('@lift') ? username.slice(0, -5) : username;

const levelKor: Record<AccountLevel, string> = {
  ADMIN: '어드민',
  DISTRIBUTOR: '총판',
  AGENCY: '대행사',
  SELLER: '셀러',
};

const searchPlaceholders: Record<SearchField, string> = {
  all: '로그 ID · 아이디 · 상위 계정 · 작업자 · 행위 통합 검색',
  keyword: '설명 검색',
  slot: '슬롯 번호 입력',
  actor: '아이디(username 또는 숫자 ID)',
  parent: '상위 계정(username 또는 숫자 ID)',
  worker: '작업자(username)',
  action: '행위 검색 (예: 생성, refund, 연장)',
  date: '로그 생성일 (YYYY-MM-DD)',
  start_date: '로그 생성일 이후 (YYYY-MM-DD)',
  end_date: '로그 생성일 이하 (YYYY-MM-DD)',
};

const accountFieldOptions: Array<{ value: AccountFilterField; label: string }> = [
  { value: 'sellerId', label: '아이디' },
  { value: 'sellerParentId', label: '상위 계정' },
  { value: 'creatorId', label: '생성자 ID' },
  { value: 'actorId', label: '행위자 ID' },
];

const isValidDateInput = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).isValid();

/** 슬롯 페이지와 같이: 화면 필터(draft) 변경만으로는 목록을 다시 가져오지 않고, 조회/검색 시 applied로 반영 후 요청 */
type LogFiltersState = {
  activeFilter: ActiveFilter;
  range: [Dayjs, Dayjs] | null;
  selectedActions: string[];
  selectedAccountId: number | undefined;
  accountFilterField: AccountFilterField;
  searchField: SearchField;
  searchKeyword: string;
  logSlotStartDate: Dayjs | null;
  logSlotEndDate: Dayjs | null;
};

const createInitialLogFilters = (): LogFiltersState => ({
  activeFilter: 'today',
  range: null,
  selectedActions: [],
  selectedAccountId: undefined,
  accountFilterField: 'sellerId',
  searchField: 'all',
  searchKeyword: '',
  logSlotStartDate: null,
  logSlotEndDate: null,
});

const hasCustomRange = (state: Pick<LogFiltersState, 'range'>): boolean =>
  !!(state.range?.[0] && state.range?.[1]);

/** 로그 일시 RangePicker가 채워져 있으면 기간(custom) 조회로 정규화 */
const normalizeLogFilters = (state: LogFiltersState): LogFiltersState =>
  hasCustomRange(state) ? { ...state, activeFilter: 'custom' } : state;

const LogsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const logs = useAppSelector(selectLogs);
  const loading = useAppSelector(selectLogLoading);
  const pagination = useAppSelector(selectLogPagination);
  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);
  const isAdmin = currentUser?.grade === 'ADMIN';
  const canDelete = canDeleteLogs(currentUser);
  const showWorkerSearch = isFullAdmin(currentUser);

  const [draft, setDraft] = useState<LogFiltersState>(() => createInitialLogFilters());
  const [applied, setApplied] = useState<LogFiltersState>(() => createInitialLogFilters());
  const [pageState, setPageState] = useState({ page: 1, pageSize: 20 });
  const [selected, setSelected] = useState<Key[]>([]);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [summary, setSummary] = useState<LogSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    if (accounts.length === 0) {
      dispatch(fetchAccounts());
    }
  }, [accounts.length, dispatch]);

  const actionOptions = useMemo(
    () =>
      Object.entries(actionMeta).map(([key, meta]) => ({
        value: key,
        label: meta.label,
      })),
    []
  );

  const accountOptions = useMemo(
    () =>
      accounts.map(account => ({
        value: account.id,
        label: `${stripLiftSuffix(account.username)} (${levelKor[(account.grade as AccountLevel)] ?? account.grade})`,
      })),
    [accounts]
  );

  const renderActionTag = useCallback((props: any) => {
    const { label, value, closable, onClose } = props;
    const color = actionMeta[String(value)]?.color ?? 'default';

    return (
      <Tag color={color} closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
        {label}
      </Tag>
    );
  }, []);

  const buildParams = useCallback(
    ({ includePagination = true }: { includePagination?: boolean } = {}): LogParams => {
      const params: LogParams = {};

      if (includePagination) {
        params.page = pageState.page;
        params.page_size = pageState.pageSize;
      }

      const keyword = applied.searchKeyword.trim();

      if (hasCustomRange(applied)) {
        params.filter_type = 'custom';
        params.start_date = applied.range![0].format('YYYY-MM-DD');
        params.end_date = applied.range![1].format('YYYY-MM-DD');
      } else if (applied.activeFilter) {
        params.filter_type = applied.activeFilter;
      }

      if (applied.logSlotStartDate) {
        params.log_start_date = applied.logSlotStartDate.format('YYYY-MM-DD');
      } else if (applied.logSlotEndDate) {
        params.log_end_date = applied.logSlotEndDate.format('YYYY-MM-DD');
      }

      if (applied.selectedActions.length > 0) {
        params.action = applied.selectedActions;
      }

      if (applied.selectedAccountId) {
        if (applied.accountFilterField === 'sellerParentId') params.sellerParentId = applied.selectedAccountId;
        if (applied.accountFilterField === 'sellerId') params.sellerId = applied.selectedAccountId;
        if (applied.accountFilterField === 'creatorId') params.creatorId = applied.selectedAccountId;
        if (applied.accountFilterField === 'actorId') params.actorId = applied.selectedAccountId;
      }

      if (keyword) {
        switch (applied.searchField) {
          case 'slot':
            params.slotId = Number(keyword);
            break;
          case 'keyword':
            params.keyword = keyword;
            break;
          case 'actor':
            if (/^\d+$/.test(keyword)) params.sellerId = Number(keyword);
            else params.actor = keyword;
            break;
          case 'parent':
            if (/^\d+$/.test(keyword)) params.parentId = Number(keyword);
            else params.parent = keyword;
            break;
          case 'worker':
            params.worker = keyword;
            break;
          case 'action':
            params.action = keyword;
            break;
          case 'date':
            params.date = keyword;
            break;
          case 'start_date':
            params.start_date = keyword;
            break;
          case 'end_date':
            params.end_date = keyword;
            break;
          case 'all':
            params.all = keyword;
            break;
        }
      }

      return params;
    },
    [applied, pageState.page, pageState.pageSize]
  );

  const requestParams = useMemo(() => buildParams(), [buildParams]);
  const summaryParams = useMemo(() => buildParams({ includePagination: false }), [buildParams]);

  const reloadLogs = useCallback(() => {
    dispatch(fetchLogs(requestParams));
  }, [dispatch, requestParams]);

  useEffect(() => {
    reloadLogs();
  }, [reloadLogs]);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const nextSummary = await logRepo.getSummary(summaryParams);
        if (!cancelled) {
          setSummary(nextSummary);
        }
      } catch {
        if (!cancelled) {
          setSummary(null);
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [summaryParams]);

  useEffect(() => {
    setSelected([]);
  }, [logs]);

  const selectedLogs = useMemo(() => {
    const logById = new Map(logs.map(log => [log.logId, log]));
    return selected
      .map(id => logById.get(Number(id)))
      .filter((log): log is Log => !!log);
  }, [logs, selected]);

  const handleRange = useCallback((value: [Dayjs | null, Dayjs | null] | null) => {
    if (!value || !value[0] || !value[1]) {
      setDraft(d => ({ ...d, range: null }));
      return;
    }

    setDraft(d => ({
      ...d,
      range: [value[0]!, value[1]!],
      activeFilter: 'custom',
    }));
  }, []);

  const commitFilters = useCallback(
    (keywordOverride?: string) => {
      const base: LogFiltersState = {
        ...draft,
        ...(keywordOverride !== undefined ? { searchKeyword: keywordOverride } : {}),
      };
      const trimmed = base.searchKeyword.trim();
      const field = base.searchField;

      if (field === 'slot' && trimmed && !/^\d+$/.test(trimmed)) {
        message.warning('슬롯 번호는 숫자로 입력해주세요.');
        return;
      }

      if (
        (field === 'date' || field === 'start_date' || field === 'end_date') &&
        trimmed &&
        !isValidDateInput(trimmed)
      ) {
        message.warning('날짜는 YYYY-MM-DD 형식으로 입력해주세요.');
        return;
      }

      if (field === 'worker' && !showWorkerSearch) {
        message.warning('작업자 검색은 ADMIN만 사용할 수 있습니다.');
        return;
      }

      const next = normalizeLogFilters({
        ...base,
        searchKeyword: trimmed,
      });
      setApplied(next);
      setDraft(next);
      setPageState(prev => ({ ...prev, page: 1 }));
    },
    [draft, showWorkerSearch],
  );

  const handlePageChange = useCallback((page: number, pageSize?: number) => {
    setPageState(prev => ({
      page,
      pageSize: pageSize ?? prev.pageSize,
    }));
  }, []);

  const applyLogSlotDateFilter = useCallback((patch: Pick<LogFiltersState, 'logSlotStartDate' | 'logSlotEndDate'>) => {
    const next = normalizeLogFilters({
      ...draft,
      ...patch,
    });
    setDraft(next);
    setApplied(next);
    setPageState(prev => ({ ...prev, page: 1 }));
  }, [draft]);

  const handleLogSlotStartDate = useCallback((date: Dayjs | null) => {
    applyLogSlotDateFilter({
      logSlotStartDate: date,
      logSlotEndDate: date ? null : draft.logSlotEndDate,
    });
  }, [applyLogSlotDateFilter, draft.logSlotEndDate]);

  const handleLogSlotEndDate = useCallback((date: Dayjs | null) => {
    applyLogSlotDateFilter({
      logSlotEndDate: date,
      logSlotStartDate: date ? null : draft.logSlotStartDate,
    });
  }, [applyLogSlotDateFilter, draft.logSlotStartDate]);

  const handleTodayLogs = useCallback(() => {
    const next: LogFiltersState = {
      ...draft,
      activeFilter: 'today',
      range: null,
      searchKeyword: '',
    };
    setDraft(next);
    setApplied(next);
    setPageState(prev => ({ ...prev, page: 1 }));
  }, [draft]);

  const handleYesterdayLogs = useCallback(() => {
    const next: LogFiltersState = {
      ...draft,
      activeFilter: 'yesterday',
      range: null,
      searchKeyword: '',
    };
    setDraft(next);
    setApplied(next);
    setPageState(prev => ({ ...prev, page: 1 }));
  }, [draft]);

  const handleThisMonthLogs = useCallback(() => {
    const next: LogFiltersState = {
      ...draft,
      activeFilter: 'this_month',
      range: null,
      searchKeyword: '',
    };
    setDraft(next);
    setApplied(next);
    setPageState(prev => ({ ...prev, page: 1 }));
  }, [draft]);

  const handleCustomRange = useCallback(() => {
    if (!draft.range?.[0] || !draft.range?.[1]) {
      message.warning('날짜 범위를 선택해주세요.');
      return;
    }
    const next = normalizeLogFilters({ ...draft, activeFilter: 'custom' });
    setDraft(next);
    setApplied(next);
    setPageState(prev => ({ ...prev, page: 1 }));
  }, [draft]);

  /** 화면 필터만 초기화. 목록/적용 조건(applied)은 그대로 두어 재요청하지 않음 */
  const handleResetFilters = useCallback(() => {
    setDraft(createInitialLogFilters());
  }, []);

  const refreshAfterDelete = useCallback(
    (deletedCount: number) => {
      if (logs.length <= deletedCount && pageState.page > 1) {
        setPageState(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }));
        return;
      }

      reloadLogs();
    },
    [logs.length, pageState.page, reloadLogs]
  );

  const handleDeleteLog = useCallback(
    (logId: number) => {
      dispatch(deleteLog(logId))
        .unwrap()
        .then(() => {
          message.success('로그가 삭제되었습니다.');
          refreshAfterDelete(1);
        })
        .catch(() => {
          message.error('로그 삭제에 실패했습니다.');
        });
    },
    [dispatch, refreshAfterDelete]
  );

  const handleBatchDelete = useCallback(() => {
    if (selected.length === 0) {
      message.warning('삭제할 로그를 선택해주세요.');
      return;
    }

    dispatch(bulkDeleteLogs(selected.map(id => Number(id))))
      .unwrap()
      .then(() => {
        message.success(`${selected.length}개 로그가 삭제되었습니다.`);
        setSelected([]);
        refreshAfterDelete(selected.length);
      })
      .catch(() => {
        message.error('로그 삭제에 실패했습니다.');
      });
  }, [dispatch, refreshAfterDelete, selected]);

  const handleDownloadLogs = useCallback(async () => {
    setExcelDownloading(true);

    try {
      let logsToExport = selectedLogs;

      if (logsToExport.length === 0) {
        const response: GetLogsResponse = await logRepo.getLogs(buildParams({ includePagination: false }));
        logsToExport = response.results;
      }

      if (logsToExport.length === 0) {
        message.warning('다운로드할 로그가 없습니다.');
        return;
      }

      const { exportLogsToExcel } = await import('../../Utils/exportLogsToExcel');
      exportLogsToExcel(logsToExport, accounts, applied.activeFilter, applied.range);
      message.success(`${logsToExport.length}개 로그가 다운로드되었습니다.`);
    } catch {
      message.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setExcelDownloading(false);
    }
  }, [accounts, applied.activeFilter, applied.range, buildParams, selectedLogs]);

  const totalCount = summary?.count ?? pagination?.total_count ?? logs.length;
  const plusDays = summary?.plus_days ?? 0;
  const minusDays = summary?.minus_days ?? 0;
  const netDays = summary?.net_days ?? 0;
  const downloadLabel =
    selected.length > 0
      ? `선택 ${selected.length}개`
      : `전체 ${totalCount.toLocaleString()}개`;

  return (
    <>
      <Title level={3}>활동 로그</Title>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Button type={draft.activeFilter === 'today' ? 'primary' : 'default'} onClick={handleTodayLogs}>
          오늘
        </Button>
        <Button type={draft.activeFilter === 'yesterday' ? 'primary' : 'default'} onClick={handleYesterdayLogs}>
          어제
        </Button>
        <Button type={draft.activeFilter === 'this_month' ? 'primary' : 'default'} onClick={handleThisMonthLogs}>
          이번달
        </Button>
        <Button type={draft.activeFilter === 'custom' ? 'primary' : 'default'} onClick={handleCustomRange}>
          기간 조회
        </Button>

        <Tooltip title="현재 페이지에 표시 중인 로그 기준 합계입니다.">
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '6px 10px',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              background: '#fafafa',
              lineHeight: 1,
            }}
          >
            <span style={{ fontWeight: 700, color: '#333' }}>
              현재 페이지 합계
              {summaryLoading && (
                <span style={{ marginLeft: 6, color: '#999', fontWeight: 400, fontSize: 12 }}>
                  <LoadingOutlined style={{ marginRight: 4 }} />
                  업데이트 중
                </span>
              )}
            </span>
            <span style={{ color: '#1677ff' }}>
              {plusDays}일
            </span>
            <span style={{ color: '#cf1322' }}>
              -{minusDays}일
            </span>
            <span style={{ color: '#333' }}>
              총 {netDays}일
            </span>
          </div>
        </Tooltip>

        <Button type="primary" icon={<SearchOutlined />} onClick={() => commitFilters()}>
          조회
        </Button>

        <Button onClick={handleDownloadLogs} loading={excelDownloading}>
          엑셀 다운로드 ({downloadLabel})
        </Button>

        <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
          조회 초기화
        </Button>

        <span style={{ color: '#666', fontSize: 13 }}>
          총 {totalCount.toLocaleString()}개 로그
        </span>

        {canDelete && selected.length > 0 && (
          <Popconfirm
            title={`${selected.length}개 로그를 삭제하시겠습니까?`}
            description="삭제된 로그는 복구할 수 없습니다."
            onConfirm={handleBatchDelete}
            okText="삭제"
            cancelText="취소"
            okType="danger"
          >
            <Button danger icon={<DeleteOutlined />}>
              선택 삭제 ({selected.length}개)
            </Button>
          </Popconfirm>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6} xl={6}>
            <RangePicker value={draft.range} onChange={handleRange} style={{ width: '100%' }} />
          </Col>

          <Col xs={24} sm={12} md={8} lg={5} xl={4}>
            <Select
              value={draft.accountFilterField}
              onChange={(value: AccountFilterField) => {
                setDraft(d => ({ ...d, accountFilterField: value }));
              }}
              style={{ width: '100%' }}
              options={accountFieldOptions}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={7} xl={7}>
            <Tooltip title="선택한 필드 기준으로 정확한 계정 ID를 서버에 전달합니다.">
              <Select
                showSearch
                allowClear
                placeholder="계정 선택"
                style={{ width: '100%' }}
                value={draft.selectedAccountId}
                optionFilterProp="label"
                options={accountOptions}
                onChange={(value) => {
                  setDraft(d => ({ ...d, selectedAccountId: typeof value === 'number' ? value : undefined }));
                }}
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Tooltip>
          </Col>

          <Col xs={24} sm={24} md={24} lg={6} xl={7}>
            <Select
              mode="multiple"
              allowClear
              placeholder="행위 선택"
              style={{ width: '100%' }}
              value={draft.selectedActions}
              onChange={(value) => {
                setDraft(d => ({ ...d, selectedActions: value as string[] }));
              }}
              options={actionOptions}
              tagRender={renderActionTag}
            />
          </Col>
        </Row>

        <Row gutter={[12, 12]} align="middle" style={{ marginTop: 12 }}>
          <Col xs={24} sm={12} md={6} lg={5} xl={4}>
            <Tooltip title="슬롯 시작일이 선택한 날짜와 정확히 일치하는 로그만 조회합니다. 종료일과 동시 사용 불가.">
              <DatePicker
                value={draft.logSlotStartDate}
                onChange={handleLogSlotStartDate}
                placeholder="슬롯 시작일"
                style={{ width: '100%' }}
                allowClear
              />
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} md={6} lg={5} xl={4}>
            <Tooltip title="슬롯 종료일이 선택한 날짜와 정확히 일치하는 로그만 조회합니다. 시작일과 동시 사용 불가.">
              <DatePicker
                value={draft.logSlotEndDate}
                onChange={handleLogSlotEndDate}
                placeholder="슬롯 종료일"
                style={{ width: '100%' }}
                allowClear
              />
            </Tooltip>
          </Col>

          <Col xs={24} md={8} lg={6} xl={5}>
            <Select
              value={draft.searchField}
              onChange={(value: SearchField) => setDraft(d => ({ ...d, searchField: value }))}
              style={{ width: '100%' }}
              options={[
                { value: 'all', label: '통합 검색' },
                { value: 'keyword', label: '설명' },
                { value: 'slot', label: '슬롯 번호' },
                { value: 'actor', label: '아이디' },
                { value: 'parent', label: '상위 계정' },
                ...(showWorkerSearch ? [{ value: 'worker' as const, label: '작업자' }] : []),
                { value: 'action', label: '행위' },
                { value: 'date', label: '로그 생성일' },
                { value: 'start_date', label: '로그 생성일 이후' },
                { value: 'end_date', label: '로그 생성일 이하' },
              ]}
            />
          </Col>

          <Col xs={24} md={16} lg={10} xl={8}>
            <Space.Compact style={{ width: '100%' }}>
              <Search
                enterButton="검색"
                allowClear
                value={draft.searchKeyword}
                placeholder={searchPlaceholders[draft.searchField]}
                onChange={(event) => setDraft(d => ({ ...d, searchKeyword: event.target.value }))}
                onSearch={(value, _event, info) => {
                  if (!value.trim() && info?.source === 'clear') return;
                  commitFilters(value);
                }}
              />
            </Space.Compact>
          </Col>
        </Row>
      </div>

      <LogTable
        data={logs}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        selected={selected}
        onSelectChange={setSelected}
        onDelete={handleDeleteLog}
      />
    </>
  );
};

export default LogsPage;
